import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import {
  BrowserRouter,
  Navigate,
  NavLink,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import {
  getNotifications,
  getSetupStatus,
  getToken,
  markAllRead,
  markNotifRead,
} from './api';
import Login from './pages/Login';
import Setup from './pages/Setup';
import MainDashboard from './pages/MainDashboard';

// Lazy-load heavy pages to keep initial bundle small
const Watchlist = React.lazy(() => import('./pages/Watchlist'));
const Hot       = React.lazy(() => import('./pages/Hot'));
const OpenTrades = React.lazy(() => import('./pages/OpenTrades'));
const History = React.lazy(() => import('./pages/History'));
const Backtest = React.lazy(() => import('./pages/Backtest'));
const Notifications = React.lazy(() => import('./pages/Notifications'));
const Settings = React.lazy(() => import('./pages/Settings'));
const KibarWatchlist = React.lazy(() => import('./pages/KibarWatchlist'));
const KibarOpenTrades = React.lazy(() => import('./pages/KibarOpenTrades'));
const KibarHistory = React.lazy(() => import('./pages/KibarHistory'));

// ─────────────────────────────────────────────
// Theme context
// ─────────────────────────────────────────────

type Theme = 'dark' | 'light';

interface ThemeContextValue {
  theme: Theme;
  toggleTheme: () => void;
}

export const ThemeContext = createContext<ThemeContextValue>({
  theme: 'dark',
  toggleTheme: () => {},
});

export function useTheme(): ThemeContextValue {
  return useContext(ThemeContext);
}

function getInitialTheme(): Theme {
  const stored = localStorage.getItem('theme') as Theme | null;
  if (stored === 'dark' || stored === 'light') return stored;
  // System preference
  if (window.matchMedia('(prefers-color-scheme: light)').matches) return 'light';
  return 'dark';
}

// ─────────────────────────────────────────────
// Notification context (unread count for badge)
// ─────────────────────────────────────────────

interface NotifContextValue {
  unreadCount: number;
  refresh: () => void;
}

const NotifContext = createContext<NotifContextValue>({
  unreadCount: 0,
  refresh: () => {},
});

export function useNotifBadge(): NotifContextValue {
  return useContext(NotifContext);
}

// ─────────────────────────────────────────────
// Protected Route wrapper
// ─────────────────────────────────────────────

function ProtectedRoute(): React.ReactElement {
  if (!getToken()) {
    return <Navigate to="/login" replace />;
  }
  return <Outlet />;
}

// ─────────────────────────────────────────────
// Sidebar navigation item
// ─────────────────────────────────────────────

interface NavItem {
  path: string;
  label: string;
  icon: string;
  badge?: number;
  href?: string; // external link — opens in new tab instead of using the router
}

function SidebarItem({ item }: { item: NavItem }): React.ReactElement {
  const sharedStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '10px 16px',
    borderRadius: '8px',
    color: 'var(--text-secondary)',
    backgroundColor: 'transparent',
    fontWeight: 400,
    fontSize: '0.9rem',
    textDecoration: 'none',
    transition: 'all 0.15s ease',
    position: 'relative',
  };

  if (item.href) {
    return (
      <a href={item.href} target="_blank" rel="noopener noreferrer" style={sharedStyle}>
        <span style={{ fontSize: '1.1rem', width: '20px', textAlign: 'center' }}>{item.icon}</span>
        <span style={{ flex: 1 }}>{item.label}</span>
        <span style={{ fontSize: '0.65rem', opacity: 0.5 }}>↗</span>
      </a>
    );
  }

  return (
    <NavLink
      to={item.path}
      end={item.path === '/'}
      style={({ isActive }) => ({
        ...sharedStyle,
        color: isActive ? 'var(--accent-blue)' : 'var(--text-secondary)',
        backgroundColor: isActive ? 'rgba(59,130,246,0.1)' : 'transparent',
        fontWeight: isActive ? 600 : 400,
      })}
    >
      <span style={{ fontSize: '1.1rem', width: '20px', textAlign: 'center' }}>
        {item.icon}
      </span>
      <span style={{ flex: 1 }}>{item.label}</span>
      {item.badge != null && item.badge > 0 && (
        <span className="badge badge-red">{item.badge > 99 ? '99+' : item.badge}</span>
      )}
    </NavLink>
  );
}

// ─────────────────────────────────────────────
// Bottom tab bar (mobile)
// ─────────────────────────────────────────────

function BottomTabItem({ item }: { item: NavItem }): React.ReactElement {
  if (item.href) {
    return (
      <a
        href={item.href}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          gap: '3px', padding: '8px 4px', flex: 1,
          color: 'var(--text-muted)', textDecoration: 'none',
          fontSize: '0.62rem', fontWeight: 400,
        }}
      >
        <span style={{ fontSize: '1.25rem' }}>{item.icon}</span>
        <span>{item.label}</span>
      </a>
    );
  }

  return (
    <NavLink
      to={item.path}
      end={item.path === '/'}
      style={({ isActive }) => ({
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '3px',
        padding: '8px 4px',
        flex: 1,
        color: isActive ? 'var(--accent-blue)' : 'var(--text-muted)',
        textDecoration: 'none',
        fontSize: '0.62rem',
        fontWeight: isActive ? 600 : 400,
        position: 'relative',
      })}
    >
      <span style={{ fontSize: '1.25rem', position: 'relative' }}>
        {item.icon}
        {item.badge != null && item.badge > 0 && (
          <span
            style={{
              position: 'absolute',
              top: '-4px',
              right: '-6px',
              background: 'var(--accent-red)',
              color: '#fff',
              fontSize: '0.55rem',
              fontWeight: 700,
              borderRadius: '999px',
              padding: '1px 4px',
              minWidth: '14px',
              textAlign: 'center',
            }}
          >
            {item.badge > 99 ? '99+' : item.badge}
          </span>
        )}
      </span>
      <span>{item.label}</span>
    </NavLink>
  );
}

// ─────────────────────────────────────────────
// Notification Bell dropdown
// ─────────────────────────────────────────────

function NotificationBell(): React.ReactElement {
  const { unreadCount, refresh } = useNotifBadge();
  const [open, setOpen] = useState(false);
  const [notifs, setNotifs] = useState<
    Array<{
      id: number;
      title: string;
      message: string;
      level: string;
      is_read: boolean;
      created_at: string;
    }>
  >([]);
  const [loading, setLoading] = useState(false);

  const loadNotifs = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    try {
      const res = await getNotifications(5, 0);
      setNotifs(res.data.items);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [open]);

  useEffect(() => {
    loadNotifs();
  }, [loadNotifs]);

  const handleRead = async (id: number) => {
    await markNotifRead(id);
    setNotifs((prev) =>
      prev.map((n) => (n.id === id ? { ...n, is_read: true } : n))
    );
    refresh();
  };

  const handleReadAll = async () => {
    await markAllRead();
    setNotifs((prev) => prev.map((n) => ({ ...n, is_read: true })));
    refresh();
  };

  const levelColor: Record<string, string> = {
    SUCCESS: 'var(--accent-green)',
    ERROR: 'var(--accent-red)',
    WARNING: 'var(--accent-orange)',
    INFO: 'var(--accent-blue)',
  };

  return (
    <div style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen((p) => !p)}
        className="btn btn-ghost btn-sm"
        style={{ fontSize: '1.1rem', padding: '6px 8px', position: 'relative' }}
        aria-label="Notifications"
      >
        🔔
        {unreadCount > 0 && (
          <span
            style={{
              position: 'absolute',
              top: '2px',
              right: '2px',
              background: 'var(--accent-red)',
              color: '#fff',
              fontSize: '0.55rem',
              fontWeight: 700,
              borderRadius: '999px',
              padding: '1px 4px',
              minWidth: '14px',
              textAlign: 'center',
            }}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <>
          {/* Backdrop */}
          <div
            style={{ position: 'fixed', inset: 0, zIndex: 49 }}
            onClick={() => setOpen(false)}
          />
          {/* Dropdown */}
          <div
            style={{
              position: 'absolute',
              top: 'calc(100% + 8px)',
              right: 0,
              zIndex: 50,
              background: 'var(--bg-card)',
              border: '1px solid var(--border)',
              borderRadius: '12px',
              width: '320px',
              boxShadow: 'var(--shadow)',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '12px 16px',
                borderBottom: '1px solid var(--border)',
              }}
            >
              <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>
                Notifications
              </span>
              {unreadCount > 0 && (
                <button
                  onClick={handleReadAll}
                  className="btn btn-ghost btn-sm"
                  style={{ fontSize: '0.75rem' }}
                >
                  Mark all read
                </button>
              )}
            </div>
            {loading ? (
              <div style={{ padding: '24px', textAlign: 'center' }}>
                <span className="spinner" />
              </div>
            ) : notifs.length === 0 ? (
              <div
                style={{
                  padding: '24px',
                  textAlign: 'center',
                  color: 'var(--text-muted)',
                  fontSize: '0.85rem',
                }}
              >
                No notifications
              </div>
            ) : (
              <div style={{ maxHeight: '340px', overflowY: 'auto' }}>
                {notifs.map((n) => (
                  <div
                    key={n.id}
                    onClick={() => !n.is_read && handleRead(n.id)}
                    style={{
                      padding: '12px 16px',
                      borderBottom: '1px solid var(--border)',
                      backgroundColor: n.is_read
                        ? 'transparent'
                        : 'rgba(59,130,246,0.05)',
                      cursor: n.is_read ? 'default' : 'pointer',
                      transition: 'background-color 0.15s ease',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '8px',
                      }}
                    >
                      <span
                        style={{
                          width: '6px',
                          height: '6px',
                          borderRadius: '50%',
                          background: n.is_read
                            ? 'transparent'
                            : levelColor[n.level] || 'var(--accent-blue)',
                          marginTop: '6px',
                          flexShrink: 0,
                        }}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            fontWeight: 600,
                            fontSize: '0.82rem',
                            color: 'var(--text-primary)',
                          }}
                        >
                          {n.title}
                        </div>
                        <div
                          style={{
                            fontSize: '0.78rem',
                            color: 'var(--text-secondary)',
                            marginTop: '2px',
                          }}
                        >
                          {n.message}
                        </div>
                        <div
                          style={{
                            fontSize: '0.7rem',
                            color: 'var(--text-muted)',
                            marginTop: '4px',
                          }}
                        >
                          {new Date(n.created_at).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <NavLink
              to="/notifications"
              onClick={() => setOpen(false)}
              style={{
                display: 'block',
                padding: '10px 16px',
                textAlign: 'center',
                fontSize: '0.82rem',
                color: 'var(--accent-blue)',
                borderTop: '1px solid var(--border)',
                textDecoration: 'none',
              }}
            >
              View all notifications
            </NavLink>
          </div>
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// AppShell – layout with sidebar / bottom tabs
// ─────────────────────────────────────────────

function AppShell(): React.ReactElement {
  const { theme, toggleTheme } = useTheme();
  const { unreadCount } = useNotifBadge();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const navItems: NavItem[] = [
    { path: '/', label: 'Dashboard', icon: '📊' },
    { path: '/watchlist', label: 'Watchlist', icon: '👁️' },
    { path: '/hot', label: 'Hot', icon: '🔥' },
    { path: '/trades', label: 'Open Trades', icon: '📈' },
    { path: '/history', label: 'History', icon: '📋' },
    { path: '/backtest', label: 'Backtest', icon: '🔬' },
    { path: '/notifications', label: 'Alerts', icon: '🔔', badge: unreadCount },
    { path: '/settings', label: 'Settings', icon: '⚙️' },
    { path: '/kibar', label: 'Kibar Watchlist', icon: '⭐' },
    { path: '/kibar-trades', label: 'Kibar Trades', icon: '📈' },
    { path: '/kibar-history', label: 'Kibar History', icon: '📋' },
    { path: '/rrg', label: 'RRG Charts', icon: '🔄', href: 'https://derhan.duckdns.org/rrg/' },
  ];

  // Page title from route
  const pageTitle = React.useMemo(() => {
    const found = navItems.find((item) =>
      item.path === '/'
        ? location.pathname === '/'
        : location.pathname.startsWith(item.path)
    );
    return found?.label || 'Trading Bot';
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return (
    <div
      style={{
        display: 'flex',
        height: '100vh',
        overflow: 'hidden',
        backgroundColor: 'var(--bg-primary)',
      }}
    >
      {/* ── Desktop Sidebar ── */}
      <aside
        className="desktop-only"
        style={{
          width: '220px',
          flexShrink: 0,
          backgroundColor: 'var(--bg-secondary)',
          borderRight: '1px solid var(--border)',
          display: 'flex',
          flexDirection: 'column',
          padding: '0',
          overflowY: 'auto',
        }}
      >
        {/* Logo */}
        <div
          style={{
            padding: '20px 20px 16px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
          }}
        >
          <span style={{ fontSize: '1.5rem' }}>⚡</span>
          <div>
            <div
              style={{
                fontWeight: 700,
                fontSize: '0.95rem',
                color: 'var(--text-primary)',
              }}
            >
              Capital Bot
            </div>
            <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
              CFD Automation
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: '2px',
            padding: '12px 8px',
          }}
        >
          {navItems.map((item) => (
            <SidebarItem key={item.path} item={item} />
          ))}
        </nav>

        {/* Theme toggle at bottom */}
        <div
          style={{
            padding: '16px 12px',
            borderTop: '1px solid var(--border)',
          }}
        >
          <button
            className="btn btn-ghost btn-sm w-full"
            onClick={toggleTheme}
            style={{ justifyContent: 'center' }}
          >
            {theme === 'dark' ? '☀️ Light Mode' : '🌙 Dark Mode'}
          </button>
        </div>
      </aside>

      {/* ── Main content area ── */}
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <header
          style={{
            backgroundColor: 'var(--bg-secondary)',
            borderBottom: '1px solid var(--border)',
            padding: '0 20px',
            height: '56px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
          }}
        >
          {/* Mobile: logo + title */}
          <div
            style={{ display: 'flex', alignItems: 'center', gap: '10px' }}
          >
            <span className="mobile-only" style={{ fontSize: '1.3rem' }}>
              ⚡
            </span>
            <h1
              style={{
                fontSize: '1rem',
                fontWeight: 600,
                color: 'var(--text-primary)',
              }}
            >
              {pageTitle}
            </h1>
          </div>

          {/* Right side actions */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {/* Theme toggle on desktop only via sidebar; on mobile show here */}
            <button
              className="mobile-only btn btn-ghost btn-sm"
              onClick={toggleTheme}
              aria-label="Toggle theme"
            >
              {theme === 'dark' ? '☀️' : '🌙'}
            </button>
            <NotificationBell />
          </div>
        </header>

        {/* Page content */}
        <main
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '20px',
            paddingBottom: '80px', // clearance for mobile bottom bar
          }}
        >
          <React.Suspense
            fallback={
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '200px',
                  gap: '12px',
                  color: 'var(--text-muted)',
                }}
              >
                <span className="spinner" />
                Loading…
              </div>
            }
          >
            <Outlet />
          </React.Suspense>
        </main>
      </div>

      {/* ── Mobile Bottom Bar (hamburger) ── */}
      <div
        className="mobile-only"
        style={{
          position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 100,
          backgroundColor: 'var(--bg-secondary)',
          borderTop: '1px solid var(--border)',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', height: '56px', padding: '0 16px' }}>
          {/* Current page label */}
          <span style={{ flex: 1, fontWeight: 600, fontSize: '0.9rem', color: 'var(--text-primary)' }}>
            {navItems.find(i => i.path === '/' ? location.pathname === '/' : location.pathname.startsWith(i.path))?.icon}{' '}
            {navItems.find(i => i.path === '/' ? location.pathname === '/' : location.pathname.startsWith(i.path))?.label || 'Trading Bot'}
          </span>
          <button
            onClick={() => setMobileMenuOpen(true)}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              padding: '8px', color: 'var(--text-primary)',
              display: 'flex', flexDirection: 'column', gap: '5px',
            }}
            aria-label="Open menu"
          >
            <span style={{ display: 'block', width: '22px', height: '2px', background: 'currentColor', borderRadius: '2px' }} />
            <span style={{ display: 'block', width: '22px', height: '2px', background: 'currentColor', borderRadius: '2px' }} />
            <span style={{ display: 'block', width: '22px', height: '2px', background: 'currentColor', borderRadius: '2px' }} />
          </button>
        </div>
      </div>

      {/* ── Mobile Menu Drawer ── */}
      {mobileMenuOpen && (
        <div
          className="mobile-only"
          style={{ position: 'fixed', inset: 0, zIndex: 200 }}
          onClick={() => setMobileMenuOpen(false)}
        >
          {/* Backdrop */}
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)' }} />

          {/* Sheet sliding up from bottom */}
          <div
            style={{
              position: 'absolute', left: 0, right: 0, bottom: 0,
              backgroundColor: 'var(--bg-secondary)',
              borderRadius: '16px 16px 0 0',
              paddingBottom: 'env(safe-area-inset-bottom, 0px)',
              maxHeight: '85vh',
              display: 'flex', flexDirection: 'column',
            }}
            onClick={e => e.stopPropagation()}
          >
            {/* Handle bar + header */}
            <div style={{ padding: '12px 20px 8px', display: 'flex', alignItems: 'center', gap: '12px', borderBottom: '1px solid var(--border)' }}>
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '1.2rem' }}>⚡</span>
                <span style={{ fontWeight: 700, fontSize: '0.95rem', color: 'var(--text-primary)' }}>Capital Bot</span>
              </div>
              <button
                onClick={() => setMobileMenuOpen(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2rem', color: 'var(--text-muted)', padding: '4px' }}
              >✕</button>
            </div>

            {/* Scrollable nav list */}
            <nav style={{ overflowY: 'auto', flex: 1, padding: '8px 12px' }}>
              {navItems.map((item) => {
                const isActive = item.path === '/'
                  ? location.pathname === '/'
                  : location.pathname.startsWith(item.path);
                return item.href ? (
                  <a
                    key={item.path}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setMobileMenuOpen(false)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '14px',
                      padding: '13px 14px', borderRadius: '10px',
                      textDecoration: 'none', color: 'var(--text-secondary)',
                      fontSize: '0.95rem',
                    }}
                  >
                    <span style={{ fontSize: '1.2rem', width: '24px', textAlign: 'center' }}>{item.icon}</span>
                    <span style={{ flex: 1 }}>{item.label}</span>
                    <span style={{ fontSize: '0.7rem', opacity: 0.45 }}>↗</span>
                  </a>
                ) : (
                  <button
                    key={item.path}
                    onClick={() => { navigate(item.path); setMobileMenuOpen(false); }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '14px',
                      width: '100%', padding: '13px 14px', borderRadius: '10px',
                      background: isActive ? 'rgba(59,130,246,0.12)' : 'transparent',
                      border: 'none', cursor: 'pointer', textAlign: 'left',
                      color: isActive ? 'var(--accent-blue)' : 'var(--text-secondary)',
                      fontWeight: isActive ? 600 : 400, fontSize: '0.95rem',
                    }}
                  >
                    <span style={{ fontSize: '1.2rem', width: '24px', textAlign: 'center' }}>{item.icon}</span>
                    <span style={{ flex: 1 }}>{item.label}</span>
                    {item.badge != null && item.badge > 0 && (
                      <span style={{
                        background: 'var(--accent-red)', color: '#fff',
                        borderRadius: '999px', padding: '1px 7px',
                        fontSize: '0.7rem', fontWeight: 700, minWidth: '18px', textAlign: 'center',
                      }}>{item.badge > 99 ? '99+' : item.badge}</span>
                    )}
                  </button>
                );
              })}
            </nav>

            {/* Theme toggle at bottom of drawer */}
            <div style={{ padding: '12px 16px', borderTop: '1px solid var(--border)' }}>
              <button
                className="btn btn-ghost btn-sm w-full"
                onClick={() => { toggleTheme(); setMobileMenuOpen(false); }}
                style={{ justifyContent: 'center' }}
              >
                {theme === 'dark' ? '☀️ Light Mode' : '🌙 Dark Mode'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// Root App component
// ─────────────────────────────────────────────

function AppProviders(): React.ReactElement {
  const [theme, setTheme] = useState<Theme>(getInitialTheme);
  const [unreadCount, setUnreadCount] = useState(0);

  // Apply theme to document root
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = useCallback(() => {
    setTheme((t) => (t === 'dark' ? 'light' : 'dark'));
  }, []);

  // Poll for unread count every 60 s
  const refreshUnread = useCallback(async () => {
    if (!getToken()) return;
    try {
      const res = await getNotifications(1, 0);
      setUnreadCount(res.data.unread_count);
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    refreshUnread();
    const id = setInterval(refreshUnread, 60_000);
    return () => clearInterval(id);
  }, [refreshUnread]);

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      <NotifContext.Provider value={{ unreadCount, refresh: refreshUnread }}>
        <RouterContent />
      </NotifContext.Provider>
    </ThemeContext.Provider>
  );
}

// ─────────────────────────────────────────────
// Route tree
// ─────────────────────────────────────────────

function RouterContent(): React.ReactElement {
  const navigate = useNavigate();

  // Check setup status on first load (only when token present)
  useEffect(() => {
    getSetupStatus()
      .then((res) => {
        if (!res.data.configured) {
          navigate('/setup', { replace: true });
        }
      })
      .catch(() => {
        // Backend unreachable – don't redirect
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/setup" element={<Setup />} />

      {/* Protected routes */}
      <Route element={<ProtectedRoute />}>
        <Route element={<AppShell />}>
          <Route path="/" element={<MainDashboard />} />
          <Route
            path="/watchlist"
            element={
              <React.Suspense fallback={null}>
                <Watchlist />
              </React.Suspense>
            }
          />
          <Route
            path="/hot"
            element={
              <React.Suspense fallback={null}>
                <Hot />
              </React.Suspense>
            }
          />
          <Route
            path="/trades"
            element={
              <React.Suspense fallback={null}>
                <OpenTrades />
              </React.Suspense>
            }
          />
          <Route
            path="/history"
            element={
              <React.Suspense fallback={null}>
                <History />
              </React.Suspense>
            }
          />
          <Route
            path="/backtest"
            element={
              <React.Suspense fallback={null}>
                <Backtest />
              </React.Suspense>
            }
          />
          <Route
            path="/notifications"
            element={
              <React.Suspense fallback={null}>
                <Notifications />
              </React.Suspense>
            }
          />
          <Route
            path="/settings"
            element={
              <React.Suspense fallback={null}>
                <Settings />
              </React.Suspense>
            }
          />
          <Route
            path="/kibar"
            element={
              <React.Suspense fallback={null}>
                <KibarWatchlist />
              </React.Suspense>
            }
          />
          <Route
            path="/kibar-trades"
            element={
              <React.Suspense fallback={null}>
                <KibarOpenTrades />
              </React.Suspense>
            }
          />
          <Route
            path="/kibar-history"
            element={
              <React.Suspense fallback={null}>
                <KibarHistory />
              </React.Suspense>
            }
          />
        </Route>
      </Route>

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App(): React.ReactElement {
  return (
    <BrowserRouter>
      <AppProviders />
    </BrowserRouter>
  );
}
